import { Channel } from "stoat.js";

export function isEphemeralCall(channel: Channel): boolean {
  return channel.type === "DirectMessage" || channel.type === "Group";
}

/**
 * Always try to recover from unexpected LiveKit disconnects.
 * Previously DM/group calls skipped reconnect entirely, so a brief blip
 * permanently ejected that participant (and often ended 1:1 calls).
 */
export function shouldAttemptReconnect(_channel: Channel): boolean {
  return true;
}

/** Grace before treating "everyone else left" as a real hang-up. */
export const AUTO_END_CALL_GRACE_MS = 8_000;

export const MAX_VOICE_RECONNECT_ATTEMPTS = 5;

export const VOICE_RECONNECT_DELAYS_MS = [
  500, 1_000, 2_000, 4_000, 8_000,
] as const;

/**
 * End a private call only when we are alone after someone else left.
 * Callers should delay this check by AUTO_END_CALL_GRACE_MS so brief
 * reconnects do not tear down the session for the remaining party.
 */
export function shouldAutoEndCall(
  channel: Channel,
  ctx: {
    selfId: string;
    leavingUserId: string;
    pendingRingingCount: number;
  },
): boolean {
  if (!isEphemeralCall(channel)) return false;
  if (ctx.leavingUserId === ctx.selfId) return false;

  const others = [...channel.voiceParticipants.keys()].filter(
    (id) => id !== ctx.selfId,
  );
  if (others.length > 0) return false;
  if (ctx.pendingRingingCount > 0) return false;

  return true;
}

/** Disconnect reasons that should not trigger a reconnect loop. */
export function isFatalVoiceDisconnect(reason: number | undefined): boolean {
  // livekit-client DisconnectReason numeric values (stable across versions):
  // CLIENT_INITIATED=0, DUPLICATE_IDENTITY=1, SERVER_SHUTDOWN=2,
  // PARTICIPANT_REMOVED=3, ROOM_DELETED=4, STATE_MISMATCH=5, JOIN_FAILURE=6
  if (reason === undefined) return false;
  return (
    reason === 1 || // DUPLICATE_IDENTITY
    reason === 3 || // PARTICIPANT_REMOVED
    reason === 4 // ROOM_DELETED
  );
}
